import { Button } from '@/components/atoms/Buttons';
import {
    createDataTableColumnHelper,
    type DataTableColumnDef,
} from '@/components/organisms/DataTable';

import { DeleteUserButton, ImpersonateUserButton } from './UserRowButtons';
import {
    getLocationSummary,
    getRoleName,
    locationAccessRank,
    type TableUser,
} from './userTableHelpers';

interface UserTableColumnsInput {
    t: (key: string, opts?: Record<string, unknown>) => string;
    clientId: number;
    /** The ids of the client's locations; empty until they load. */
    clientLocationIds: readonly number[];
    canManageAccess: boolean;
    canImpersonateUsers: boolean;
    canDeleteUsers: boolean;
    impersonateUser: (id: number) => Promise<unknown>;
    deleteUser: (id: number) => Promise<unknown>;
    onManageAccess: (user: TableUser) => void;
}

/** The user table's columns: who, role and confirmation, then the actions the viewer may take. */
export function buildUserTableColumns(
    input: UserTableColumnsInput
): DataTableColumnDef<TableUser>[] {
    const {
        t,
        clientId,
        clientLocationIds,
        canManageAccess,
        canImpersonateUsers,
        canDeleteUsers,
        impersonateUser,
        deleteUser,
        onManageAccess,
    } = input;
    const columnHelper = createDataTableColumnHelper<TableUser>();
    const cols: DataTableColumnDef<TableUser>[] = columnHelper.columns([
        columnHelper.accessor(
            (row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email,
            {
                id: 'name',
                header: t('common.labels.name'),
                enableGlobalFilter: true,
            }
        ),
        columnHelper.accessor((row) => row.email, {
            id: 'email',
            header: t('userAccess.common.eMail'),
            enableGlobalFilter: true,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor((row) => getRoleName(row, clientId, t), {
            id: 'role',
            header: t('userAccess.common.role'),
            enableGlobalFilter: true,
        }),
        columnHelper.accessor((row) => row.emailConfirmed, {
            id: 'confirmed',
            header: t('userAccess.userTableColumns.confirmed'),
            cell: (info) =>
                info.getValue() ? t('userAccess.userTableColumns.confirmedText') : null,
        }),
    ]);

    if (clientId && canManageAccess) {
        cols.push(
            ...columnHelper.columns([
                // The value sorts; the text is drawn in the cell, from the current locations.
                columnHelper.accessor((row) => locationAccessRank(row, clientId), {
                    id: 'locations',
                    header: t('userAccess.locationsColumn'),
                    enableGlobalFilter: false,
                    cell: (info) =>
                        getLocationSummary(info.row.original, clientId, clientLocationIds, t),
                }),
            ])
        );
        cols.push(
            columnHelper.display({
                id: 'manageAccess',
                header: t('userAccess.manageAccess'),
                enableGlobalFilter: false,
                cell: (info) => {
                    const user = info.row.original;
                    if (user.systemRole === 'ADMIN') return null;
                    return (
                        <Button
                            variant="secondary"
                            trackingId="user_table_manage_access"
                            onClick={() => onManageAccess(user)}>
                            {t('userAccess.manageAccess')}
                        </Button>
                    );
                },
            })
        );
    }

    if (canImpersonateUsers) {
        cols.push(
            columnHelper.display({
                id: 'impersonate',
                header: t('userAccess.loginAsUser'),
                enableGlobalFilter: false,
                cell: (info) => (
                    <ImpersonateUserButton
                        impersonateUser={impersonateUser}
                        id={info.row.original.id}
                    />
                ),
            })
        );
    }

    if (canDeleteUsers) {
        cols.push(
            columnHelper.display({
                id: 'delete',
                header: t('userAccess.common.deleteUser'),
                enableGlobalFilter: false,
                cell: (info) => (
                    <DeleteUserButton deleteUser={deleteUser} id={info.row.original.id} />
                ),
            })
        );
    }

    return cols;
}
